import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Space, Typography, Button } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { VIP_STATUS_LABELS, VIP_STATUS_COLORS, VipStatus } from '../types';
import apiClient from '../services/api';

const { Title } = Typography;

// Interface for Alert user data
interface AlertUser {
  account_id: string;
  full_name: string;
  email: string;
  specialty: string;
  prefecture: string;
  clinic_name: string;
  vip_status: number;
  alert_count: number;
  last_alert_time: string;
  image_uploaded_count: number;
}

interface AlertUsersResponse {
  alert_users: AlertUser[];
  total_count: number;
}

const AlertUsersList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AlertUser[]>([]);

  useEffect(() => {
    fetchAlertUsers();
  }, []);

  const fetchAlertUsers = async () => {
    try {
      setLoading(true);
      const response: AlertUsersResponse = await apiClient.get('/api/analytics/alert-users');
      console.log('Alert users response:', response);
      setData(response.alert_users);
    } catch (error) {
      console.error('Failed to fetch alert users:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<AlertUser> = [
    {
      title: 'ユーザー名',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 150,
      ellipsis: true,
      render: (name: string, record: AlertUser) => (
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            marginBottom: '2px',
            flexWrap: 'wrap'
          }}>
            <span style={{ 
              fontWeight: 'bold',
              fontSize: '12px',
              color: name === '退会済み' ? 'red' : 'inherit'
            }}>
              {name || `ユーザー${record.account_id.slice(-8)}`}
            </span>
            <Tag 
              color={VIP_STATUS_COLORS[record.vip_status as VipStatus]}
              style={{ fontSize: '9px', padding: '0 4px', lineHeight: '16px' }}
            >
              {VIP_STATUS_LABELS[record.vip_status as VipStatus]}
            </Tag>
          </div>
          <div style={{ fontSize: '10px', color: '#666', wordBreak: 'break-all' }}>
            {record.email}
          </div>
        </div>
      ),
    },
    {
      title: 'クリニック',
      dataIndex: 'clinic_name',
      key: 'clinic_name',
      width: 120,
      ellipsis: true,
      render: (clinic: string, record: AlertUser) => (
        <span 
          style={{ 
            color: '#1890ff', 
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '12px'
          }}
          onClick={() => {
            if (clinic && clinic !== 'なし') {
              const searchQuery = record.prefecture ? 
                `${clinic} ${record.prefecture}` : 
                clinic;
              const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
              window.open(googleSearchUrl, '_blank');
            }
          }}
        >
          {clinic || 'なし'}
        </span>
      ),
    },
    {
      title: 'アラート回数',
      dataIndex: 'alert_count',
      key: 'alert_count',
      width: 90,
      align: 'center',
      render: (count: number) => (
        <Tag color="red" style={{ fontSize: '10px', padding: '0 4px' }}>
          {count}回
        </Tag>
      ),
    },
    {
      title: '画像数',
      dataIndex: 'image_uploaded_count',
      key: 'image_uploaded_count',
      width: 70,
      align: 'center',
      render: (count: number) => <span style={{ fontSize: '11px' }}>{count}枚</span>,
    },
    {
      title: '最終アラート日時',
      dataIndex: 'last_alert_time',
      key: 'last_alert_time',
      width: 120,
      render: (date: string) => {
        if (!date) return <span style={{ fontSize: '11px', color: '#999' }}>不明</span>;
        const formattedDate = new Date(date).toLocaleDateString('ja-JP', {
          month: 'numeric',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        return <span style={{ fontSize: '11px' }}>{formattedDate}</span>;
      },
    },
  ];

  return (
    <Card 
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Title level={4} style={{ margin: 0 }}>上限アラート発生ユーザー</Title>
            <span style={{ color: '#999', fontSize: '14px' }}>Alert Users List</span>
          </Space>
          <Button
            type="text"
            icon={<ExpandOutlined />}
            onClick={() => window.open('/fullscreen/alert-users', '_blank')}
            style={{ marginLeft: 'auto' }}
          />
        </div>
      }
      style={{ height: '100%', minHeight: '400px' }}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Table
          loading={loading}
          columns={columns}
          dataSource={data}
          rowKey="account_id"
          pagination={{
            pageSize: 5,
            showSizeChanger: false,
            showTotal: (total) => `全 ${total} 件`,
            size: 'small',
            style: { marginBottom: 0, marginTop: '8px' }
          }}
          scroll={{ x: 'max-content' }}
          size="small"
          style={{ fontSize: '12px' }}
        />
      </div>
    </Card>
  );
};

export default AlertUsersList;